import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSelectModule } from '@angular/material/select';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { IndexTemplateComponent } from '@bisual/ngx-index-template';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { UserListDialogComponent } from 'src/app/components/main/shared/user-list-dialog/user-list-dialog.component';
import { TypeSafeMatCellDeferred } from 'src/app/directives/type-safe-mat-cell-def.directive';
import { SurveyStatusEnum, SurveyStatusEnumLabels } from 'src/app/enums/survey-status.enum';
import { SurveyVariableConditionFrequencyEnumLabels } from 'src/app/enums/survey-variable-condition-frequency.enum';
import { ArrayResponse } from 'src/app/models/array_response.interface';
import { Survey, SurveyKpiProgress, SurveyKpis, SurveyLatestWaveProgress, SurveySerialized } from 'src/app/models/survey.model';
import { UserListDialogData } from 'src/app/models/user-list-dialog.model';
import { ApiService, UtilsService } from 'src/app/services';
import { SurveyKpiCardComponent } from './survey-kpi-card/survey-kpi-card.component';

@Component({
  selector: 'app-surveys-list',
  templateUrl: './surveys-list.component.html',
  styleUrl: './surveys-list.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    RouterLink,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatOptionModule,
    MatSelectModule,
    MatPaginatorModule,
    MatTableModule,
    MatTooltipModule,
    MatProgressBarModule,
    TypeSafeMatCellDeferred,
    SurveyKpiCardComponent,
  ],
  providers: [DatePipe],
})
export class SurveysListComponent extends IndexTemplateComponent implements OnInit {
  protected readonly page_title = inject(ActivatedRoute).snapshot.title;

  private readonly api_service = inject(ApiService);
  private readonly change_detector_ref = inject(ChangeDetectorRef);
  private readonly date_pipe = inject(DatePipe);
  private readonly destroy_ref = inject(DestroyRef);
  private readonly dialog = inject(MatDialog);
  protected readonly form_builder = inject(UntypedFormBuilder);
  protected readonly router = inject(Router);
  protected readonly activated_route = inject(ActivatedRoute);
  protected readonly utils_service = inject(UtilsService);

  data_source = new MatTableDataSource<SurveySerialized>([]);

  displayed_columns: string[] = ['title', 'status', 'frequency', 'latest_wave_progress', 'actions'];

  readonly status_filter_options: { value: SurveyStatusEnum | ''; label: string }[] = [
    { value: '', label: 'Todos' },
    { value: SurveyStatusEnum.Active, label: SurveyStatusEnumLabels[SurveyStatusEnum.Active] },
    { value: SurveyStatusEnum.Paused, label: SurveyStatusEnumLabels[SurveyStatusEnum.Paused] },
    { value: SurveyStatusEnum.Finished, label: SurveyStatusEnumLabels[SurveyStatusEnum.Finished] },
  ];

  protected active_surveys_count: number | null = null;
  protected latest_wave_value: string | null = null;
  protected latest_wave_hint: string | null = null;
  protected latest_wave_progress: SurveyKpiProgress | null = null;
  protected next_wave_value: string | null = null;
  protected next_wave_hint: string | null = null;

  constructor() {
    super(inject(Router), inject(UntypedFormBuilder), inject(ActivatedRoute), inject(UtilsService));

    this.filterFormExtraParams = {
      search: [''],
      status: [this.activated_route.snapshot.queryParamMap.get('status') ?? SurveyStatusEnum.Active],
    };
  }

  override ngOnInit(): void {
    this.fetchKpis();
    super.ngOnInit();
    this.listenSearchFormControl();
    this.listenStatusFormControl();
  }

  override fetchData(): void {
    const search_term = this.filterForm.get('search')?.value || '';
    const status = this.filterForm.get('status')?.value as SurveyStatusEnum | '';

    const params: Record<string, unknown> = {
      page: this.filterForm.get('page')?.value ?? 1,
      per_page: this.filterForm.get('per_page')?.value ?? this.pageSize,
      order_by: 'title:asc',
      with: 'current_survey_variable_condition',
      append: 'latest_wave_progress',
    };

    if (status !== '') {
      params.status = status;
    }

    if (search_term) {
      params.search = search_term;
    }

    this.api_service
      .getSurveys(params)
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: (response: ArrayResponse<Survey>): void => {
          this.data_source.data = this.serializeSurveys(response.data);
          this.setMetadata(response.meta.total, response.meta.current_page - 1, response.meta.per_page);
          this.change_detector_ref.markForCheck();
        },
      });
  }

  protected openProgressDetail(progress: SurveyLatestWaveProgress): void {
    const dialog_data: UserListDialogData = {
      title: 'Respuestas de la remesa',
      subtitle: `${progress.completed_count}/${progress.total_count} completadas`,
      users: progress.users.map(user => ({
        id: user.id,
        full_name: user.full_name,
        pokemon_profile_picture_url: user.pokemon_profile_picture_url,
        serious_profile_picture_url: user.serious_profile_picture_url,
        status: user.status,
        status_label: user.status_label,
        meta_label: user.total_questions_count <= 0 ? user.status_label : `${user.answered_count}/${user.total_questions_count} preguntas`,
      })),
    };

    this.dialog.open(UserListDialogComponent, {
      data: dialog_data,
      width: '420px',
      maxWidth: '92vw',
      autoFocus: false,
      panelClass: 'user-list-dialog',
    });
  }

  private fetchKpis(): void {
    this.api_service
      .getSurveysKpis()
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: (kpis: SurveyKpis): void => {
          this.setKpis(kpis);
          this.change_detector_ref.markForCheck();
        },
      });
  }

  private setKpis(kpis: SurveyKpis): void {
    const latest = kpis.latest_wave;
    const next = kpis.next_wave;

    this.active_surveys_count = kpis.active_surveys_count;
    this.latest_wave_value = latest?.survey_title ?? null;
    this.latest_wave_hint = latest ? this.buildLatestWaveHint(latest.wave_title, latest.generated_at) : null;
    this.latest_wave_progress = latest ? { completed: latest.completed_count, total: latest.total_count } : null;
    this.next_wave_value = next ? this.formatDate(next.estimated_at) : null;
    this.next_wave_hint = next?.survey_title ?? null;
  }

  private buildLatestWaveHint(wave_title: string, generated_at: string): string {
    const date_label = this.formatDate(generated_at);

    return date_label ? `${wave_title} · ${date_label}` : wave_title;
  }

  private listenSearchFormControl(): void {
    this.filterForm
      .get('search')
      ?.valueChanges.pipe(debounceTime(500), distinctUntilChanged(), takeUntilDestroyed(this.destroy_ref))
      .subscribe((): void => {
        this.filterForm.patchValue({ page: 1 }, { emitEvent: false });
        this.fetchData();
      });
  }

  private listenStatusFormControl(): void {
    this.filterForm
      .get('status')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe((): void => {
        this.filterForm.patchValue({ page: 1 }, { emitEvent: false });
        this.fetchData();
      });
  }

  private serializeSurveys(surveys: Survey[]): SurveySerialized[] {
    return surveys.map((survey: Survey): SurveySerialized => {
      const progress = survey.latest_wave_progress;
      const frequency = survey.current_survey_variable_condition?.frequency;
      const percent = progress && progress.total_count > 0 ? Math.round((progress.completed_count / progress.total_count) * 100) : null;

      return {
        ...survey,
        status_label: SurveyStatusEnumLabels[survey.status] ?? survey.status,
        frequency_label: frequency ? SurveyVariableConditionFrequencyEnumLabels[frequency] : '—',
        latest_wave_progress_percent: percent,
        latest_wave_progress_label: progress ? `${progress.completed_count}/${progress.total_count}` : '—',
      };
    });
  }

  private formatDate(value: string): string | null {
    return this.date_pipe.transform(value, 'd MMM yyyy', undefined, 'es-ES');
  }
}
